import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useChatStore } from "@/hooks/useChatStore";

/** How long the panel blocks on a prewarm before showing the session anyway. */
const PREWARM_BLOCKING_MS = 1500;

/**
 * Which session the panel is currently blocking on while its agent client
 * warms up. There is one chat panel, so this is a single app-wide fact and it
 * lives outside React: session activation runs from an effect, and mutating a
 * module store there (rather than calling setState) keeps that effect free of
 * the cascading re-render React warns about.
 */
let blockingSessionId: string | null = null;
const listeners = new Set<() => void>();

function setBlocking(next: string | null): void {
  if (blockingSessionId === next) return;
  blockingSessionId = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => blockingSessionId;

/**
 * Warms a session's agent client, and reports which session that is currently
 * blocking the panel. `prewarmSession` is fire-and-forget; the blocking window
 * is capped so a slow backend can't hold the panel in its loading state.
 */
export function useSessionPrewarm(orgId: string | null) {
  const chatStore = useChatStore();
  const blockingPrewarmSessionId = useSyncExternalStore(subscribe, getSnapshot);
  const timerRef = useRef<number | null>(null);
  const sequenceRef = useRef(0);

  const clearBlocking = useCallback((sessionId: string, sequence: number) => {
    if (sequenceRef.current !== sequence) return;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (blockingSessionId === sessionId) setBlocking(null);
  }, []);

  const runSessionPrewarm = useCallback(
    (session: { id: string; messageCount: number }) => {
      if (!orgId || session.messageCount === 0) return Promise.resolve(null);

      const enableWebSearch = chatStore.getSessionWebSearch(session.id);
      // WHA-3829: the project scope is part of the prewarm config — a scoped
      // session must prewarm a client built with the scoped prompt/tools.
      const sessionProject = chatStore.getSessionProject(session.id);
      const prewarmState = chatStore.getSessionPrewarmState(
        session.id,
        enableWebSearch,
        sessionProject?.id
      );
      if (prewarmState === "ready" || prewarmState === "context_only") {
        return Promise.resolve(null);
      }

      const sequence = ++sequenceRef.current;
      setBlocking(session.id);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(
        () => clearBlocking(session.id, sequence),
        PREWARM_BLOCKING_MS
      );

      return chatStore
        .prewarmSession({
          sessionId: session.id,
          enableWebSearch,
          projectId: sessionProject?.id,
        })
        .catch(() => null)
        .finally(() => clearBlocking(session.id, sequence));
    },
    [chatStore, orgId, clearBlocking]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      setBlocking(null);
    };
  }, []);

  return { blockingPrewarmSessionId, runSessionPrewarm };
}
